import { Bullet, Circle, ICircleSettings, Label, Template, p50, percent } from "@amcharts/amcharts5";
import {
  AxisRendererX,
  AxisRendererY,
  ILineSeriesSettings,
  LineSeries,
  ValueAxis,
  XYChart,
  XYCursor,
  XYSeries,
} from "@amcharts/amcharts5/xy";
import { isNil, map, omitBy, orderBy } from "lodash";

import BaseChart, { LegendData } from "@/classes/BaseChart";
import Lang from "@/lang";
import { axisTitle } from "@/settings/charts";
import { chartFontSettings, chartInitialSettings } from "@/settings/widgets";
import { ChartDataRecord, TooltipSeries } from "@/types/charts";

interface MinMax {
  [key: string]: number | undefined;
}

type BubbleSeries = { name: string; data?: LineSeries };

const minMaxDefault = () => ({ xAxis: undefined, yAxis: undefined, displayMetric: undefined });

const DOT_SIZE_STEP = 3;
const EXTRA_X_SPACE = 0.03;
const EXTRA_Y_MIN = 0.15;
const EXTRA_Y_MAX = 0.2;

export default class BubbleChart extends BaseChart {
  min: MinMax = minMaxDefault();
  max: MinMax = minMaxDefault();
  bubbleSeries!: BubbleSeries[];
  isScatterPlot!: boolean;

  initChart() {
    this.isScatterPlot = this.chartName === "scatterPlot";

    this.root.container.children.clear();

    if (this.chart) {
      (this.chart as XYChart).series.clear();
    }

    this.createChart();

    this.createAxes();
    this.bubbleSeries = this.bubbleSeries.map((el, index) => {
      return { name: el.name, data: this.createSeries(el.name, index) };
    });

    this.showLegend(map(this.bubbleSeries, "name"), (this.chart as XYChart).series.values as LegendData, false);

    this.createCursor();
  }

  protected mapData(data: ChartDataRecord[]): ChartDataRecord[] {
    this.bubbleSeries = [];
    const mappedData: ChartDataRecord[] = [];
    this.min = minMaxDefault();
    this.max = minMaxDefault();

    data = data.map((x) => (x[this.settings.displayMetric] === null ? { ...x, [this.settings.displayMetric]: 0 } : x));
    data = orderBy(data, [this.settings.displayMetric], ["desc"]) as ChartDataRecord[];

    data = this.showItems(data, this.settings.decompositionMetric, this.settings.displayMetric);

    data.forEach((item) => {
      const x: number = item[this.settings.xAxis] === null ? 0 : (item[this.settings.xAxis] as number);
      const y: number = item[this.settings.yAxis] === null ? 0 : (item[this.settings.yAxis] as number);
      const value: number = item[this.settings.displayMetric] as number;

      const minMaxValues: MinMax = { xAxis: x, yAxis: y, displayMetric: value };

      Object.keys(minMaxDefault()).forEach((key) => {
        const value = minMaxValues[key] as number;

        if (this.max[key] === undefined || value > (this.max[key] as number)) {
          this.max[key] = value;
        }

        if (this.min[key] === undefined || value < (this.min[key] as number)) {
          this.min[key] = value;
        }
      });

      const seriesName: string = (item[this.settings.decompositionMetric] =
        (item[this.settings.decompositionMetric] as string) ?? Lang.null);

      this.bubbleSeries.push({ name: seriesName });
      mappedData.push(omitBy({ x, y, value, name: seriesName }, isNil));
    });
    return mappedData;
  }

  protected createSeries(name: string, index: number) {
    const seriesSettings: ILineSeriesSettings = {
      name,
      calculateAggregates: true,
      xAxis: this.axes.x,
      yAxis: this.axes.y,
      valueXField: "x",
      valueYField: "y",
      fill: this.getSeriesColor(index),
    };

    if (!this.isScatterPlot) {
      seriesSettings.valueField = "value";
    }

    const oneSeries = (this.chart as XYChart).series.push(LineSeries.new(this.root, seriesSettings));

    const circleTemplate = Template.new({}) as Template<Circle>;
    const circleSettings: ICircleSettings = {
      fill: oneSeries.get("fill"),
    };

    if (this.isScatterPlot) {
      circleSettings.radius = DOT_SIZE_STEP * this.settings.dotSize;
    }

    oneSeries.bullets.push(() => {
      const graphics = Circle.new(this.root, circleSettings, circleTemplate);
      return Bullet.new(this.root, {
        sprite: graphics,
      });
    });

    if (!this.isScatterPlot) {
      oneSeries.set("heatRules", [
        {
          target: circleTemplate,
          min: 5,
          max: 40,
          minValue: this.min["displayMetric"],
          maxValue: this.max["displayMetric"],
          dataField: "value",
          key: "radius",
        },
      ]);
    }

    oneSeries.strokes.template.set("strokeOpacity", 0);

    this.createTooltip(oneSeries as TooltipSeries, () => {
      const tooltipValues = oneSeries.get("tooltip")?.dataItem?.dataContext as Record<string, unknown>;

      return tooltipValues
        ? [
            { key: this.settings.decompositionMetric, value: oneSeries.get("name") as string },
            { key: this.settings.displayMetric, value: tooltipValues?.value as number },
            { key: this.settings.xAxis, value: tooltipValues?.[oneSeries.get("valueXField") || ""] as number },
            { key: this.settings.yAxis, value: tooltipValues?.[oneSeries.get("valueYField") || ""] as number },
          ].filter((item) => !isNil(item.key))
        : [];
    });
    const currentCategory = this.data.filter((item: ChartDataRecord) => item["name"] === name);
    oneSeries.data.setAll(currentCategory);

    return oneSeries;
  }

  protected createChart() {
    this.chart = this.root.container.children.push(
      XYChart.new(this.root, {
        panX: true,
        panY: true,
        layout: this.layout,
        ...chartInitialSettings,
      })
    );
  }

  protected createAxes() {
    const xRenderer = AxisRendererX.new(this.root, {
      minGridDistance: 60,
    });
    xRenderer.labels.template.setAll({
      ...chartFontSettings.label,
      minPosition: 0.01,
    });

    const x = (this.chart as XYChart).xAxes.push(
      ValueAxis.new(this.root, {
        renderer: xRenderer,
        extraMin: EXTRA_X_SPACE,
        extraMax: EXTRA_X_SPACE,
        strictMinMaxSelection: true,
        numberFormat: this.getCompactAxesMetricFormatter(this.settings.xAxis, this.data),
      })
    );

    const yRenderer = AxisRendererY.new(this.root, {});
    yRenderer.labels.template.setAll({
      ...chartFontSettings.label,
      minPosition: 0.1,
    });
    const y = (this.chart as XYChart).yAxes.push(
      ValueAxis.new(this.root, {
        renderer: yRenderer,
        extraMin: EXTRA_Y_MIN,
        extraMax: EXTRA_Y_MAX,
        strictMinMaxSelection: true,
        numberFormat: this.getCompactAxesMetricFormatter(this.settings.yAxis, this.data),
      })
    );
    if (this.settings.xAxisTitle) {
      x.children.push(
        Label.new(this.root, {
          text: this.settings.xAxisTitle,
          x: p50,
          marginTop: axisTitle.marginTop,
          centerX: p50,
          ...chartFontSettings.axisTitle,
        })
      );
    }

    if (this.settings.yAxisTitle) {
      y.children.push(
        Label.new(this.root, {
          text: this.settings.yAxisTitle,
          ...chartFontSettings.axisTitle,
          x: percent(-50),
          rotation: -90,
          y: percent(50),
          centerX: p50,
        })
      );
    }

    this.axes = { x, y };
  }

  protected createCursor() {
    (this.chart as XYChart).set(
      "cursor",
      XYCursor.new(this.root, {
        xAxis: this.axes.x,
        yAxis: this.axes.y,
        snapToSeries: map(this.bubbleSeries, "data") as XYSeries[],
      })
    );
  }
}
